import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { formatCivilDate, isCivilDate } from '@eis/core';
import { StatTiles, type StatTile } from '@eis/patterns';
import { useGroupPolicyPremiumSummary, type PremiumSummaryBasis } from '../api/premium';
import { NOT_STATED, formatAmount, periodLabelOf, taxTotalOf } from '../model/format';
import { isPeriodSummary } from '../model/summary';
import { GroupPremiumSummaryDialog } from './GroupPremiumSummaryDialog';

/**
 * The group policy's premium, computed by the SERVICE over ALL members (SDD-021 §8.1, PC-19):
 * the member count, net, tax and gross — for a day (monthly equivalents) or a period (pro rata) —
 * and a way into the *Aufschlüsselung*. It replaces the members tab's "Summe der geladenen Zeilen":
 * a sum over the rows a scroll happened to load is a wrong number with a right-looking label, and
 * nothing here is summed in the browser (prompt 29 Decision 9).
 */
export function GroupPremiumSummary({
  code,
  basis,
  locale,
}: {
  readonly code: string;
  readonly basis: PremiumSummaryBasis;
  readonly locale: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const query = useGroupPolicyPremiumSummary(code, basis);
  const [open, setOpen] = useState(false);
  const summary = query.data?.summary;

  if (query.isError) {
    return (
      <Alert
        severity="error"
        data-testid="premium-summary-error"
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              void query.refetch();
            }}
          >
            {t('premium.summary.retry')}
          </Button>
        }
      >
        {t('premium.summary.error')}
      </Alert>
    );
  }

  if (summary === undefined) {
    const placeholder = <Skeleton variant="text" width={96} />;
    return (
      <Stack spacing={1} data-testid="premium-summary-loading" aria-busy="true">
        <StatTiles
          label={t('premium.summary.label')}
          items={[
            { label: t('premium.summary.members'), value: placeholder },
            { label: t('premium.summary.net'), value: placeholder },
            { label: t('premium.summary.tax'), value: placeholder },
            { label: t('premium.summary.gross'), value: placeholder },
          ]}
        />
      </Stack>
    );
  }

  const currency = summary.currency ?? 'EUR';
  const money = (value: string | undefined): string => formatAmount(value, currency, locale);
  const total = summary.total;
  const taxTotal = taxTotalOf(total);
  const tiles: StatTile[] = [
    { label: t('premium.summary.members'), value: String(summary.memberCount ?? 0) },
    { label: t('premium.summary.net'), value: money(total?.net) },
    {
      label: t('premium.summary.tax'),
      // One figure when the service states one; per code when there are several and no total.
      value:
        taxTotal !== undefined
          ? money(taxTotal)
          : (total?.tax ?? []).length > 0
            ? (total?.tax ?? []).map((tax) => `${tax.code ?? ''} ${money(tax.amount)}`).join(' · ')
            : NOT_STATED,
    },
    { label: t('premium.summary.gross'), value: money(total?.gross), tone: 'accent' },
  ];

  return (
    <Stack spacing={1} data-testid="premium-summary">
      <StatTiles items={tiles} label={t('premium.summary.label')} />
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}
      >
        <Typography variant="body2" color="text.secondary" data-testid="premium-summary-basis">
          {isPeriodSummary(summary)
            ? t(
                'premium.summary.forPeriod',
                periodLabelOf(
                  summary.basis?.periodFrom ?? '',
                  summary.basis?.periodTo ?? '',
                  locale,
                ),
              )
            : t('premium.summary.atDay', { date: dayLabelOf(summary.basis?.validAt, locale) })}
          {summary.oneTime?.gross !== undefined &&
            ` ${t('premium.summary.oneTime', { amount: money(summary.oneTime.gross) })}`}
        </Typography>
        <Button
          size="small"
          onClick={() => {
            setOpen(true);
          }}
        >
          {t('premium.summary.breakdown')}
        </Button>
      </Stack>
      <GroupPremiumSummaryDialog
        summary={summary}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        locale={locale}
      />
    </Stack>
  );
}

/** The day a summary answers, as the reader writes it. */
function dayLabelOf(day: string | undefined, locale: string): string {
  return isCivilDate(day) ? formatCivilDate(day, locale) : (day ?? '');
}
